import React, { useEffect } from 'react';
import { useState } from 'react';
import { useAppContext } from '../../Services/AppContext';
import moment from 'moment-timezone';
import TimePicker from '../Base/TimePicker/TimePicker';
import DatePicker from '../Base/DatePicker/DatePicker';
import FormControl from '../Form/FormControl';
import SubmitButton from '../Button/SubmitButton';
import Form from '../Form/Form';
import apiClient from '../../Services/ApiClient';
import * as yup from 'yup';
import RemoveButton from '../Button/RemoveButton';
import withForm from '../Form/withForm';
import { useFormContext } from '../Form/FormContext';
import useNotification from '../../Hooks/useNotification.hook';
import { useEffectAsync } from '../../helpers';
import MultiSelect from '../Base/MultiSelect/MultiSelect';
import { WORKPLACES } from '../../Modules/Workplace/Graphql/workplaces.graphql';
import { WORKPLACE_DOCTORS_QUERY } from '../../Modules/Doctor/Graphql/workplace-doctors.query';
import Select from '../Base/Select/Select';
import { WORKPLACE_MEMBERS } from '../../Modules/Workplace/Graphql/workplace-members.graphql';
import { CREATE_WORKPLACE_MEMBER_ABSENCE } from '../../Modules/Workplace/Graphql/create-workplace-member-absence.mutation.graphql';
import Checkbox from '../Base/Checkbox/Checkbox';
import TextField from '../Base/TextField/TextField';
import { getMomentDate } from '../../Utils/dates.util';
import RadioList from '../Base/RadioList/RadioList';

interface CalendarAbsenceEventProps {
	selectedDate: any;
	onSuccess?: () => void;
	loggedUserId?: string;
	order?: any;
	params?: any;
}

const CalendarAbsenceEvent = (props: CalendarAbsenceEventProps) => {
	const { selectedDate, onSuccess, order, params } = props;
	const { translation, userData } = useAppContext();
	const { formMethods } = useFormContext();
	const { showNotificationAlert, showNotificationSuccess } = useNotification();
	const [loading, setLoading] = useState(!!order);
	const [disabled, setDisabled] = useState(false);
	const [loggedUserWorkplacesIds, setLoggedUserWorkplacesIds] = useState([]);
	const [selectedUserWorkplaces, setSelectedUserWorkplaces] = useState([]);
	const [selectedWorkplacesIds, setSelectedWorkplacesIds] = useState([]);
	const [workplaceMembers, setWorkplaceMembers] = useState([]);
	const [wholeDay, setWholeDay] = useState(false);
	const [defaultValues, setDefaultValues] = useState({
		dateFrom: selectedDate,
		dateTo: selectedDate,
		timeFrom: selectedDate,
		allowContactInAbsence: false,
	});

	// @ts-ignore
	useEffectAsync(async () => {
		await handleLoadLoggedUserWorkplaces();
	}, []);

	// @ts-ignore
	useEffectAsync(
		async x => {
			await handleLoadWorkplaceMembers();
		},
		[loggedUserWorkplacesIds],
	);

	// @ts-ignore
	const handleLoadWorkplaceMembers = async () => {
		const tempWorkplaceMembers = [];

		const { meta, data } = await apiClient.graphql({
			query: WORKPLACE_DOCTORS_QUERY,
			variables: {
				workplaceIds: loggedUserWorkplacesIds,
				roleKeys: ['doctor', 'nurse', 'therapist'],
				take: 999,
			},
			fetchPolicy: 'network-only',
		});

		for (const workplaceDoctor of data?.workplaceDoctors?.data || []) {
			// @ts-ignore
			const twmE = tempWorkplaceMembers.find(
				w => w.value === workplaceDoctor?.user?.id,
			);
			if (!twmE) {
				tempWorkplaceMembers.push({
					value: workplaceDoctor?.user?.id,
					label: workplaceDoctor?.user?.profile?.fullName,
				});
			}
		}

		setWorkplaceMembers(tempWorkplaceMembers);
	};

	// @ts-ignore
	const handleLoadLoggedUserWorkplaces = async () => {
		const tempWorkplacesIds = [];

		const { meta, data } = await apiClient.graphql({
			query: WORKPLACES,
			variables: {
				type: 'normal',
			},
			fetchPolicy: 'network-only',
		});

		for (const workplace of data.workplaces.data || []) {
			tempWorkplacesIds.push(workplace.id);
		}

		setLoggedUserWorkplacesIds(tempWorkplacesIds);
	};

	// @ts-ignore
	const handleSelectWorkplaceMember = async values => {
		if (!formMethods) {
			return;
		}
		const formValues = formMethods.getValues();
		const tempWorkplaces = [];

		const { meta, data } = await apiClient.graphql({
			query: WORKPLACE_MEMBERS,
			variables: {
				userId: formValues?.workplaceMember.value,
				workplaceIds: loggedUserWorkplacesIds,
			},
			fetchPolicy: 'network-only',
		});

		for (const workplace of data.workplaceMembers?.data || []) {
			const item = {
				id: workplace.id,
				value: workplace.id,
				title: workplace.workplace?.doctorDisplayName,
				label: workplace.workplace?.doctorDisplayName,
			};
			tempWorkplaces.push(item);
		}

		setSelectedUserWorkplaces(tempWorkplaces);
	};

	// @ts-ignore
	const handleSelectWorkplaces = async values => {
		const tempWorkplacesIds = [];

		for (const workplace of values || []) {
			tempWorkplacesIds.push(workplace.value);
		}
		setSelectedWorkplacesIds(tempWorkplacesIds);
	};

	// @ts-ignore
	const handleCreate = async values => {
		setDisabled(true);

		const timeFrom = getMomentDate(values.timeFrom);

		const timeTo = getMomentDate(values.timeTo);

		let { dateFrom, dateTo, reason, allowContactInAbsence } = values;

		try {
			const result = await apiClient.graphqlMutate({
				mutation: CREATE_WORKPLACE_MEMBER_ABSENCE,
				variables: {
					workplaceMemberIds: selectedWorkplacesIds,
					start: !wholeDay
						? moment(dateFrom)
								.clone()
								.startOf('day')
								.add(timeFrom.hours(), 'hours')
								.add(timeFrom.minutes(), 'minutes')
						: moment(dateFrom)
								.clone()
								.startOf('day')
								.add('00', 'hours')
								.add('00', 'minutes'),
					end: !wholeDay
						? moment(dateTo)
								.clone()
								.startOf('day')
								.add(timeTo.hours(), 'hours')
								.add(timeTo.minutes(), 'minutes')
						: moment(dateTo)
								.clone()
								.startOf('day')
								.add('23', 'hours')
								.add('59', 'minutes'),
					description: reason,
					allowContactInAbsence,
				},
				fetchPolicy: 'network-only',
			});

			showNotificationSuccess({
				content: translation['base.absenceCreated'],
			});

			onSuccess && onSuccess();
		} catch (e) {
			setDisabled(false);
			if (e.response.data.displayableError) {
				showNotificationAlert({
					content: e.response.data.message,
				});
			}
		}
	};

	// @ts-ignore
	const handleRemove = async function () {
		await apiClient.delete('/events/cancel/' + order.event.id);
		onSuccess && onSuccess();
	};

	// @ts-ignore
	const handleUpdate = async function (values) {
		let {
			dateFrom,
			dateTo,
			timeFrom,
			timeTo,
			workplaceMember,
			reason,
			allowContactInAbsence,
		} = values;
		timeFrom = getMomentDate(timeFrom);
		timeTo = getMomentDate(timeTo);
		let event = await apiClient.patch('/event/' + order.event.id, {
			dateFrom: moment(dateFrom)
				.clone()
				.startOf('day')
				.add(timeFrom.hours(), 'hours')
				.add(timeFrom.minutes(), 'minutes'),
			dateTo: moment(dateTo)
				.clone()
				.startOf('day')
				.add(timeTo.hours(), 'hours')
				.add(timeTo.minutes(), 'minutes'),
			eventMembers: [
				{
					workplaceMemberId: workplaceMember.value,
				},
			],
			description: reason,
			allowContactInAbsence,
		});

		onSuccess && onSuccess();
	};

	// @ts-ignore
	const handleToggleWholeDay = async () => {
		setWholeDay(prev => !prev);
	};

	useEffect(() => {
		if (!order) {
			return;
		}

		const tempDefaultValues = {
			dateFrom: moment(order.event.start),
			dateTo: moment(order.event.end),
			timeFrom: moment(order.event.start),
			timeTo: moment(order.event.end),
			workplaceMember: {
				value: order.event?.extendedProps?.doctor?.id,
				label: order.event?.extendedProps?.doctor?.name,
			},
			reason: order.event?.extendedProps?.note,
			allowContactInAbsence: order.event?.extendedProps.meta.find(
				(item: { key: string }) => item.key === 'allowContactInAbsence',
			).value,
		};

		setDefaultValues(tempDefaultValues);

		setLoading(false);
	}, [order]);

	const validationSchema = yup.object({
		workplaceMember: yup
			.mixed()
			.required(translation['validation.calendarAbsenceEvent.workplaceMember']),
		workplaces: !order
			? yup
					.mixed()
					.required(translation['validation.calendarAbsenceEvent.workplaces'])
			: yup.mixed().notRequired(),
		dateFrom: yup
			.date()
			.required(translation['validation.calendarAbsenceEvent.dateFrom']),
		dateTo: yup
			.date()
			.required(translation['validation.calendarAbsenceEvent.dateTo']),
		timeFrom: yup
			.date()
			.required(translation['validation.calendarAbsenceEvent.timeFrom']),
		timeTo: yup.date().when('wholeDay', {
			is: false,
			then: yup
				.date()
				.required(translation['validation.calendarAbsenceEvent.timeTo']),
		}),
		reason: yup
			.string()
			.required(translation['validation.calendarAbsenceEvent.reason']),
	});

	return (
		<>
			{!loading && (
				<Form defaultValues={defaultValues} schema={validationSchema}>
					<div>
						<FormControl
							label='base.selectWorker'
							name='workplaceMember'
							onChange={handleSelectWorkplaceMember}
							isRequired={true}>
							<Select
								options={workplaceMembers}
								placeholder={translation['base.shared.pick']}
								isSearchable={true}
							/>
						</FormControl>
						{!order && (
							<>
								<FormControl
									label='base.chooseWorkplace'
									name='workplaces'
									onChange={handleSelectWorkplaces}
									isRequired={true}>
									<MultiSelect
										options={selectedUserWorkplaces}
										isSearchable={true}
										placeholder={translation['base.chooseWorkplace']}
										noOptionsMessage={translation['base.pickWorkerFist']}
									/>
								</FormControl>
								<FormControl
									name='wholeDay'
									onChange={handleToggleWholeDay}
									hideRequired={true}>
									<Checkbox tx={'base.wholeDay'} />
								</FormControl>
							</>
						)}
						<div className={'row'}>
							<div className={'col-6'}>
								<FormControl
									label='base.shared.dateFrom'
									name='dateFrom'
									isRequired={true}>
									<DatePicker />
								</FormControl>
								<FormControl
									label='base.shared.dateTo'
									name='dateTo'
									isRequired={true}>
									<DatePicker />
								</FormControl>
							</div>
							{!wholeDay && (
								<div className={'col-6'}>
									<FormControl
										label='base.shared.timeFrom'
										name='timeFrom'
										isRequired={true}>
										<TimePicker />
									</FormControl>
									<FormControl
										label='base.shared.timeTo'
										name='timeTo'
										isRequired={true}>
										<TimePicker />
									</FormControl>
								</div>
							)}
						</div>
						<FormControl
							label='component.calendar.absence.reason'
							name='reason'
							isRequired>
							<TextField />
						</FormControl>
						<FormControl
							label='component.calendar.absence.allowContactInAbsence'
							name='allowContactInAbsence'
							isRequired>
							<RadioList
								data={[
									{
										title: 'base.yes',
										value: true,
									},
									{
										title: 'base.no',
										value: false,
									},
								]}
							/>
						</FormControl>
						<div
							className={'d-flex flex-row align-items-center mt-4'}
							style={{ gap: '15px' }}>
							{!order ? (
								<SubmitButton
									onSubmit={handleCreate}
									title='base.create'
									disabled={disabled}
								/>
							) : (
								<>
									<SubmitButton
										onSubmit={handleUpdate}
										title={'base.shared.edit'}
									/>
									<RemoveButton onClick={handleRemove} />
								</>
							)}
						</div>
					</div>
				</Form>
			)}
		</>
	);
};

export default withForm(CalendarAbsenceEvent);
